import { useMemo } from 'react';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  filterUsableObjects,
  generateJsonSchemaConfig,
  UsableObjectConfig,
} from 'dashboard/lib/workflows/objects';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import { WorkflowActionSendCaseMessageV1 } from 'dashboard/models/workflow-version';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { JsonSchemaStringifiableTypes } from 'lib/fields';

const SEND_MESSAGE_TYPE = [
  {
    label: 'New message',
    value: 'new',
  },
  {
    label: 'Reply to existing message',
    value: 'reply',
  },
];

interface ParamsSendCaseMessageV1Props {
  caseTemplates: WorkflowActionMeta['case-templates'];
  editable: boolean;
  errors: WorkflowError[];
  usableObjects: UsableObjectConfig[];
  params: WorkflowActionSendCaseMessageV1['params'];
  updateActionParams: (newParams: WorkflowActionSendCaseMessageV1['params']) => void;
}

const ParamsSendCaseMessageV1 = ({
  caseTemplates,
  editable,
  errors,
  usableObjects,
  params,
  updateActionParams,
}: ParamsSendCaseMessageV1Props) => {
  const cases = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [WorkflowFieldObjectKey.Case],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  const caseMessages = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [WorkflowFieldObjectKey.CaseMessage],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const selectCase = (kase: WorkflowActionSendCaseMessageV1['params']['case']) => {
    updateActionParams({
      ...params,
      case: kase,
    });
  };

  const selectCaseMessage = (
    caseMessage: WorkflowActionSendCaseMessageV1['params']['case-message'],
  ) => {
    updateActionParams({
      ...params,
      'case-message': caseMessage,
    });
  };

  const selectSendType = (type: WorkflowActionSendCaseMessageV1['params']['send-message-type']) => {
    updateActionParams({
      ...params,
      'send-message-type': type,
    });
  };

  const selectCaseMessageTemplate = (
    caseMessageTemplate: WorkflowActionSendCaseMessageV1['params']['case-message-template-token'],
  ) => {
    updateActionParams({
      ...params,
      'case-message-template-token': caseMessageTemplate,
    });
  };

  const updateToEmailAddresses = (
    toEmailAddresses: WorkflowActionSendCaseMessageV1['params']['to-email-addresses'],
  ) => {
    updateActionParams({
      ...params,
      'to-email-addresses': toEmailAddresses,
    });
  };

  const updateCcEmailAddresses = (
    ccEmailAddresses: WorkflowActionSendCaseMessageV1['params']['additional-cc-email-addresses'],
  ) => {
    updateActionParams({
      ...params,
      'additional-cc-email-addresses': ccEmailAddresses,
    });
  };

  const possibleEmailTemplates = caseTemplates.flatMap((ct) =>
    ct['case-message-templates'].map((et) => ({
      label: `${ct.name} - ${et.name}`,
      value: et.token,
    })),
  );

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionSendCaseMessageV1['params']>(errors);
  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Send as' htmlFor='params-send-case-message__send-message-type' />
      <Select
        value={params['send-message-type']}
        onValueChange={selectSendType}
        id='params-send-case-message__send-message-type'
        disabled={!editable}
        hasError={hasErrorFor('send-message-type')}
      >
        {SEND_MESSAGE_TYPE.map((template) => (
          <Option key={template.value} value={template.value}>
            {template.label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('send-message-type')}
      <Spacer height={2} />
      {params['send-message-type'] === 'new' && (
        <>
          <ParamLabel labelText='Case' htmlFor='params-send-case-message__case-input' />
          <Select
            value={params.case}
            onValueChange={selectCase}
            id='params-send-case-message__case-input'
            disabled={!editable}
            hasError={hasErrorFor('case')}
          >
            {cases.map((kase) => (
              <Option key={kase.value} value={kase.value}>
                {kase.label}
              </Option>
            ))}
          </Select>
          {renderErrorMessageFor('case')}
        </>
      )}
      {params['send-message-type'] === 'reply' && (
        <>
          <ParamLabel
            labelText='Case Message'
            helpText='Options are available when trigger is case-message.sent or case-message.received'
            htmlFor='params-send-case-message__case-message-input'
          />
          <Select
            value={params['case-message']}
            onValueChange={selectCaseMessage}
            id='params-send-case-message__case-message-input'
            disabled={!editable}
            hasError={hasErrorFor('case-message')}
          >
            {caseMessages.map((kase) => (
              <Option key={kase.value} value={kase.value}>
                {kase.label}
              </Option>
            ))}
          </Select>
          {renderErrorMessageFor('case-message')}
        </>
      )}
      <Spacer height={2} />
      <ParamLabel labelText='To' htmlFor='params-send-case-message__to-email-addresses-input' />
      <HandlebarFormControl
        size='md'
        id='params-send-case-message__to-email-addresses-input'
        type='input'
        value={params['to-email-addresses']?.[0] || ''}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        allowedTypes={JsonSchemaStringifiableTypes}
        editable={editable}
        updateValue={(value: string) => updateToEmailAddresses([value])}
        hasError={hasErrorFor('to-email-addresses')}
      />
      {renderErrorMessageFor('to-email-addresses')}
      <Spacer height={2} />
      <ParamLabel
        labelText='Cc'
        helpText='A list of additional email addresses to cc aside from the case message template settings'
        htmlFor='params-send-case-message__cc-input'
      />
      <HandlebarFormControl
        size='md'
        id='params-send-case-message__cc-input'
        type='input'
        value={params['additional-cc-email-addresses']?.[0] || ''}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        allowedTypes={JsonSchemaStringifiableTypes}
        editable={editable}
        updateValue={(value: string) => updateCcEmailAddresses([value])}
        hasError={hasErrorFor('additional-cc-email-addresses')}
      />
      {renderErrorMessageFor('additional-cc-email-addresses')}
      <Spacer height={2} />
      <ParamLabel
        labelText='Case Message Template'
        htmlFor='params-send-case-message__case-message-template-input'
      />
      <Select
        value={params['case-message-template-token']}
        onValueChange={selectCaseMessageTemplate}
        id='params-send-case-message__case-message-template-input'
        disabled={!editable}
        hasError={hasErrorFor('case-message-template-token')}
      >
        {possibleEmailTemplates.map((template) => (
          <Option key={template.value} value={template.value}>
            {template.label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('case-message-template-token')}
    </>
  );
};

export default ParamsSendCaseMessageV1;
